import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { useParams } from 'react-router-dom'

import {
  fetchSite,
  fetchSiteUsers,
  createSiteUser,
  createSiteUsers,
  updateSiteUser,
  remindSiteUser,
  remindSiteUsers,
  resetBulkAddUsers,
  clearSites,
} from 'store/sitesManager'
import {
  selectSiteById,
  selectSiteUsersBySiteId,
  selectSitesIsLoading,
  selectSiteUsersIsLoading,
  selectIsBulkOperationCompleted,
  selectBulkAddUsersResponse,
} from 'store/sitesManager/selector'
import {
  deleteReports,
  fetchLessonProgressReport,
  fetchTrainingReport,
} from 'store/reports'
import ErrorBoundary from 'components/ErrorBoundary'
import Loading from 'components/Loading/Loading'
import Site from './component'
import { filterCanceledUsers, UpdateSiteUserNotifications } from 'lib/util'
import { RootState, SiteUser } from 'types'

const SiteContainer: React.FC = () => {
  const dispatch = useDispatch()
  const { id } = useParams<{ id: string }>()
  const siteId = parseInt(id, 10)
  const [showEditModal, setShowEditModal] = useState(false)

  // Combined selector to reduce re-renders
  const {
    site,
    siteUsers,
    isLoadingSite,
    isLoadingSiteUsers,
    errorMessage,
    isBulkOperationCompleted,
    addBulkUsersResponse,
    reports,
    isFetchingReports,
  } = useSelector((state: RootState) => {
    const sitesManagerState = state.sitesManager
    const reportsManagerState = state.reportsManager
    return {
      site: selectSiteById(sitesManagerState, siteId) || null,
      siteUsers: selectSiteUsersBySiteId(sitesManagerState, siteId) || null,
      isLoadingSite: selectSitesIsLoading(sitesManagerState),
      isLoadingSiteUsers: selectSiteUsersIsLoading(sitesManagerState),
      errorMessage: sitesManagerState?.error || null,
      isBulkOperationCompleted:
        selectIsBulkOperationCompleted(sitesManagerState),
      addBulkUsersResponse:
        selectBulkAddUsersResponse(sitesManagerState) || null,
      reports: reportsManagerState?.reports || {},
      isFetchingReports: reportsManagerState?.isFetching || {},
    }
  })

  // Memoize error object to prevent infinite loops
  const error = useMemo(() => {
    return errorMessage ? new Error(errorMessage) : undefined
  }, [errorMessage])

  // Fetch data when needed
  useEffect(() => {
    if (!site && !isLoadingSite && !errorMessage && siteId) {
      dispatch(fetchSite(siteId))
    }
    if (!siteUsers && !isLoadingSiteUsers && !errorMessage && siteId) {
      dispatch(fetchSiteUsers(siteId))
    }
    return () => {
      dispatch(deleteReports())
    }
  }, [
    siteId,
    site,
    isLoadingSite,
    isLoadingSiteUsers,
    siteUsers,
    errorMessage,
    dispatch,
  ])

  const backToSearch = useCallback(() => {
    dispatch(clearSites())
    dispatch(push('/css-admin/sites'))
  }, [dispatch])

  const handleGrantNewRole = useCallback(
    (siteUser: SiteUser, newRole: string) => {
      dispatch(
        updateSiteUser(
          { ...siteUser, roles: [newRole] },
          UpdateSiteUserNotifications.grantNewRole,
        ),
      )
    },
    [dispatch],
  )

  const handleCancelUserAccess = useCallback(
    (siteUser: SiteUser) => {
      dispatch(
        updateSiteUser(
          { ...siteUser, isActive: false },
          UpdateSiteUserNotifications.removeUser,
        ),
      )
    },
    [dispatch],
  )

  const filteredSiteUsers = useMemo(() => {
    return siteUsers ? filterCanceledUsers(siteUsers) : null
  }, [siteUsers])

  const isLoading = isLoadingSite || isLoadingSiteUsers

  return (
    <ErrorBoundary>
      <Loading isLoading={isLoading} message="Loading Site" />
      <Site
        addBulkUsersResponse={addBulkUsersResponse}
        backToSearch={backToSearch}
        createSiteUser={(siteUser, sendEmail, selectedSite) =>
          dispatch(createSiteUser(siteUser, sendEmail, selectedSite))
        }
        createSiteUsers={(siteId, users, sendEmail) =>
          dispatch(createSiteUsers(siteId, users, sendEmail))
        }
        error={error}
        fetchLessonProgressReport={(siteId, skuCode) =>
          dispatch(fetchLessonProgressReport(siteId, skuCode))
        }
        fetchTrainingReport={(emails, skuCode, siteName) =>
          dispatch(fetchTrainingReport(emails, skuCode, siteName))
        }
        handleCancelUserAccess={handleCancelUserAccess}
        handleGrantNewRole={handleGrantNewRole}
        isBulkOperationCompleted={isBulkOperationCompleted}
        isFetchingReports={isFetchingReports}
        remindSiteUser={siteUser => dispatch(remindSiteUser(siteUser))}
        remindSiteUsers={(siteId, emailFilters) =>
          dispatch(remindSiteUsers(siteId, emailFilters))
        }
        reports={reports}
        resetBulkAddUsers={() => dispatch(resetBulkAddUsers())}
        showEditModal={showEditModal}
        site={site}
        siteUsers={filteredSiteUsers}
        toggleEditModal={() => setShowEditModal(prev => !prev)}
      />
    </ErrorBoundary>
  )
}

export default SiteContainer
